import React, { useEffect, useState, createContext, useContext, useCallback } from 'react';
import { useAuth } from './Auth';
import SocketV1Service from '../services/socket/socket-v1.service';
import SocketV2Service from '../services/socket/socket-v2.service';
import RabbitStompService from '../services/rabbit-stomp/rabbit-stomp.service';
import { rabbitmqConfig } from '../config/rabbitmq';
import { drainActionOutbox } from '../services/actions/action-sender';
import { toast } from 'react-toastify';

interface ServicesStatus {
  isInitialized: boolean;
  socketV1Ready: boolean;
  socketV2Ready: boolean;
  rabbitStompReady: boolean;
  error: string | null;
}

const ServicesContext = createContext<ServicesStatus>({
  isInitialized: false,
  socketV1Ready: false,
  socketV2Ready: false,
  rabbitStompReady: false,
  error: null,
});

export const ServicesInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const [status, setStatus] = useState<ServicesStatus>({
    isInitialized: false,
    socketV1Ready: false,
    socketV2Ready: false,
    rabbitStompReady: false,
    error: null,
  });

  const initializeServices = useCallback(async () => {
    try {
      setStatus(prev => ({ ...prev, isInitialized: true, error: null }));

      // Configuração SocketV1
      // const socketV1Config = {
      //   url: process.env.REACT_APP_MIKE_API || 'http://localhost:3000',
      //   options: {
      //     transports: ['websocket', 'polling'] as Array<'websocket' | 'polling'>,
      //     reconnection: true,
      //     reconnectionDelay: 1000,
      //     reconnectionDelayMax: 5000,
      //     timeout: 10000,
      //     pingTimeout: 60000,
      //     pingInterval: 25000,
      //   },
      // };

      // Configuração SocketV2
      const socketV2Config = {
        url: process.env.REACT_APP_MIKE_API_V2 || 'http://localhost:3001',
        options: {
          path: '/ws/',
          transports: ['websocket', 'polling'] as Array<'websocket' | 'polling'>,
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 10000,
          pingTimeout: 60000,
          pingInterval: 25000,
          auth: { token },
        },
      };

      // Inicializar SocketV1
      // try {
      //   const socketV1 = SocketV1Service.getInstance(socketV1Config);
      //   socketV1.connect();
      //   setStatus(prev => ({ ...prev, socketV1Ready: true }));
      // } catch (error) {
      //   toast.error(`Erro ao inicializar socket Mike-V1: ${error instanceof Error ? error.message : error}`);
      //   setStatus(prev => ({ ...prev, error: `SocketV1: ${error}` }));
      // }

      // Inicializar SocketV2
      try {
        // getInstance ignora a config quando a instância já existe, então uma
        // renovação de token precisa ser aplicada explicitamente.
        const alreadyInitialized = SocketV2Service.hasInstance();
        const socketV2 = SocketV2Service.getInstance(socketV2Config);
        if (alreadyInitialized) {
          socketV2.updateAuth(token);
        }
        socketV2.connect();
        setStatus(prev => ({ ...prev, socketV2Ready: true }));
      } catch (error) {
        toast.error(`Erro ao inicializar socket Mike-V2: ${error instanceof Error ? error.message : error}`);
        setStatus(prev => ({ ...prev, error: `SocketV2: ${error}` }));
      }

      // Inicializar RabbitStomp
      try {
        const rabbitStomp = RabbitStompService.getInstance(rabbitmqConfig);
        rabbitStomp.connect();
        setStatus(prev => ({ ...prev, rabbitStompReady: true }));
      } catch (error) {
        toast.error(`Erro ao inicializar RabbitStomp: ${error instanceof Error ? error.message : error}`);
        setStatus(prev => ({ ...prev, error: `RabbitStomp: ${error}` }));
      }

    } catch (error) {
      toast.error(`Erro geral na inicialização dos services: ${error instanceof Error ? error.message : error}`);
      setStatus(prev => ({ 
        ...prev, 
        error: `Erro geral: ${error instanceof Error ? error.message : error}` 
      }));
    }
  }, [token]);

  const resetServices = useCallback(() => {
    try {
      SocketV2Service.resetInstance();
      RabbitStompService.resetInstance();
      
      setStatus({
        socketV1Ready: true,
        isInitialized: false,
        socketV2Ready: false,
        rabbitStompReady: false,
        error: null,
      });
      
    } catch (error) {
      toast.error(`Erro ao resetar services: ${error instanceof Error ? error.message : error}`);
    }
  }, []);

  useEffect(() => {
    if (token) {
      initializeServices();
    } else {
      resetServices();
    }
  }, [token, initializeServices, resetServices]);

  // Reenvia as actions que ficaram no outbox sempre que a conexão volta.
  useEffect(() => {
    if (!status.socketV2Ready || !SocketV2Service.hasInstance()) return;

    const socketV2 = SocketV2Service.getInstance();
    const handleConnect = () => {
      void drainActionOutbox();
    };

    socketV2.on('connect', handleConnect);
    if (socketV2.isConnected()) handleConnect();

    return () => socketV2.off('connect', handleConnect);
  }, [status.socketV2Ready]);

  return (
    <ServicesContext.Provider value={status}>
      {children}
    </ServicesContext.Provider>
  );
};

// Hook para acessar status dos services
export const useServicesStatus = (): ServicesStatus => {
  const context = useContext(ServicesContext);
  if (!context) {
    throw new Error('useServicesStatus deve ser usado dentro de ServicesInitializer');
  }
  return context;
};

// Hook para verificar se services estão prontos
export const useServicesReady = (): boolean => {
  const { isInitialized, socketV1Ready, socketV2Ready, rabbitStompReady } = useServicesStatus();
  return isInitialized && (socketV1Ready || socketV2Ready || rabbitStompReady);
};

// Hook para aguardar services específicos
export const useWaitForServices = (
  requiredServices: Array<'socketV1' | 'socketV2' | 'rabbitStomp'> = []
): boolean => {
  const status = useServicesStatus();
  
  if (!status.isInitialized) return false;
  
  if (requiredServices.length === 0) return true;
  
  return requiredServices.every(service => {
    switch (service) {
      case 'socketV1': return status.socketV1Ready;
      case 'socketV2': return status.socketV2Ready;
      case 'rabbitStomp': return status.rabbitStompReady;
      default: return false;
    }
  });
};

